import { addMonths } from "date-fns"

export default class Subscription {
  static STATUS_PENDING = 1
  static STATUS_DONE = 2
  static STATUS_STOPED = 3
  static STATUS_TRIALING = 4
  static STATUS_PAST_DUE = 5

  static METHOD_CARD = 1
  static METHOD_MANUAL = 2


  static FORCE_STATUS_NONE = 0
  static FORCE_STATUS_START = 1
  static FORCE_STATUS_STOP = 2

  readonly id: number
  readonly purchasePrice: number
  readonly pricePlan: string
  readonly price: number
  readonly purchaseType: 'life_time' | 'each_month'
  readonly status: number
  readonly subscriptionMethod: number
  readonly createdAt: Date
  readonly paiedAt: Date | null
  readonly expiredAt: Date | null
  readonly nextPay: Date | null
  readonly isActive: boolean
  readonly premiumServiceId: number
  readonly premiumServiceName: string
  readonly premiumServiceDescription: string
  readonly premiumServiceUrl: string
  readonly premiumServicePrice: number | null
  readonly installmentCountCurrent: number
  readonly installmentCountTotal: number
  readonly forceStatus: number
  readonly endDate: Date | null
  readonly stripeCustomerDefaultCardNumber: string
  readonly stripeCustomerDefaultCardExpiredAt: string
  readonly needMembershipFee: boolean
  readonly membershipFee: number
  readonly membershipFeePaidAt: Date | null
  readonly membershipFeeChargeAt: Date | null
  readonly membershipFeeChargeFailed: boolean
  readonly membershipFeeChargeFailedMessage: string

  constructor(data: { [key: string]: any }) {
    this.id = data.id
    this.purchasePrice = data.purchasePrice
    this.pricePlan = data.pricePlan
    this.price = data.price
    this.purchaseType = data.purchaseType
    this.status = data.status
    this.subscriptionMethod = data.subscriptionMethod
    this.createdAt = new Date(data.createdAt)
    this.paiedAt = data.paiedAt ? new Date(data.paiedAt) : null
    this.expiredAt = data.expiredAt ? new Date(data.expiredAt) : null
    this.isActive = data.isActive
    this.premiumServiceId = data.premiumServiceId
    this.premiumServiceName = data.premiumServiceName
    this.premiumServiceDescription = data.premiumServiceDescription
    this.premiumServiceUrl = data.premiumServiceUrl
    this.premiumServicePrice = data.premiumServicePrice ?? null
    this.installmentCountCurrent = data.installmentCountCurrent
    this.installmentCountTotal = data.installmentCountTotal
    this.forceStatus = data.forceStatus
    this.nextPay = data.nextPay
    this.endDate = data.endDate ? new Date(data.endDate) : null
    this.stripeCustomerDefaultCardNumber = data.stripeCustomerDefaultCardNumber
    this.stripeCustomerDefaultCardExpiredAt = data.stripeCustomerDefaultCardExpiredAt
    this.needMembershipFee = data.needMembershipFee
    this.membershipFee = data.membershipFee
    this.membershipFeePaidAt = data.membershipFeePaidAt ? new Date(data.membershipFeePaidAt) : null
    this.membershipFeeChargeAt = data.membershipFeeChargeAt ? new Date(data.membershipFeeChargeAt) : null
    this.membershipFeeChargeFailed = data.membershipFeeChargeFailed
    this.membershipFeeChargeFailedMessage = data.membershipFeeChargeFailedMessage
  }

  public get isPending(): boolean {
    return this.status === Subscription.STATUS_PENDING
  }

  public get isDone(): boolean {
    return this.status === Subscription.STATUS_DONE
  }

  public get isStoped(): boolean {
    return this.status === Subscription.STATUS_STOPED || this.status === 5
  }

  public get isTrailing(): boolean {
    return this.status === Subscription.STATUS_TRIALING
  }

  public get isByCard(): boolean {
    return this.subscriptionMethod === Subscription.METHOD_CARD
  }

  public get isByManual(): boolean {
    return this.subscriptionMethod === Subscription.METHOD_MANUAL
  }

  public get isLifeTime(): boolean {
    return this.purchaseType == 'life_time'
  }

  public get isEachMonth(): boolean {
    return this.purchaseType == 'each_month'
  }

  public get hasInstallment(): boolean {
    return this.installmentCountTotal > 1
  }

  public get isForceStatusNone(): boolean {
    return this.forceStatus === Subscription.FORCE_STATUS_NONE
  }

  public get isForceStatusStart(): boolean {
    return this.forceStatus === Subscription.FORCE_STATUS_START
  }

  public get isForceStatusStop(): boolean {
    return this.forceStatus === Subscription.FORCE_STATUS_STOP
  }

  public get needUpdateCard(){
    return this.isPending
  }


  // public get nextPay() : Date | null {
  //   if (this.isEachMonth || (this.installmentCountCurrent < this.installmentCountTotal)) {
  //     return addMonths(this.paiedAt, 1)
  //   }
  //   return null
  // }

  public get lifeTimeInvoice(){
    if (!this.isLifeTime) {
      return null
    }

    return new StripeInvoice({
      id: '',
      amountPaid: this.purchasePrice,
      date: this.paiedAt.getTime() / 1000
    })
  }
}

export class StripeUpcomingInvoice {
  readonly id: string
  readonly amountDue: number
  readonly nextPaymentAttempt: Date
  
  readonly lines: {
    data: Array<{
      amount: number
      subscription?: any
      description?: string
    }>
  }
  readonly totalCount: number

  constructor(data: { [key: string]: any }) {
    this.id = data.id
    this.amountDue = data.amountDue
    this.nextPaymentAttempt = new Date(data.nextPaymentAttempt * 1000)
    this.lines = data.lines || { data: [] }
    this.totalCount = data.lines?.data?.length || 0
  }
}

export class StripeInvoice {
  readonly id: string
  readonly amountPaid: number
  readonly amountDue: number
  readonly status: string
  readonly paid: boolean
  readonly created: Date
  readonly hostedInvoiceUrl: string
  readonly lines: {
    data: Array<{
      amount: number
      subscription?: any
      description?: string
    }>
  }
  readonly totalCount: number
  readonly amountRefunded: number

  constructor(data: { [key: string]: any }) {
    this.id = data.id
    this.amountPaid = data.amountPaid
    this.amountDue = data.amountDue
    this.status = data.status
    this.paid = data.paid
    this.amountRefunded = data.amountRefunded
    this.created = new Date(data.created * 1000)
    this.hostedInvoiceUrl = data.hostedInvoiceUrl
    this.lines = data.lines || { data: [] }
    this.totalCount = data.lines?.data?.length || 0
  }
}

export class SubscriptionInvoice {
  readonly id: string
  readonly amountPaid: number
  readonly paiedAt: Date
  constructor(data: { [key: string]: any }) {
    this.id = data.id
    this.amountPaid = data.amountPaid
    this.paiedAt = new Date(data.paiedAt)
  }
}
